export const PROFESSIONAL_PERMISSION_KEYS = {
  RESERVATIONS: "professional.reservas",
  CLIENTS: "professional.clientes",
  CLIENT_HISTORY: "professional.historial_clientes",
  LEAVES: "professional.yosoyhuman",
  SCHEDULE: "professional.horario"
} as const;

export type ProfessionalPermissionKey =
  (typeof PROFESSIONAL_PERMISSION_KEYS)[keyof typeof PROFESSIONAL_PERMISSION_KEYS];

export const PUBLIC_PATHS = new Set(["/login"]);
export const NO_ACCESS_ROUTE = "/sin-acceso";

const DEFAULT_ROUTE_ORDER: ProfessionalPermissionKey[] = [
  PROFESSIONAL_PERMISSION_KEYS.RESERVATIONS,
  PROFESSIONAL_PERMISSION_KEYS.LEAVES,
  PROFESSIONAL_PERMISSION_KEYS.SCHEDULE,
  PROFESSIONAL_PERMISSION_KEYS.CLIENTS,
  PROFESSIONAL_PERMISSION_KEYS.CLIENT_HISTORY
];

const DEFAULT_ROUTE_BY_PERMISSION: Record<ProfessionalPermissionKey, string> = {
  [PROFESSIONAL_PERMISSION_KEYS.RESERVATIONS]: "/reservas",
  [PROFESSIONAL_PERMISSION_KEYS.CLIENTS]: "/clientes",
  [PROFESSIONAL_PERMISSION_KEYS.CLIENT_HISTORY]: "/clientes",
  [PROFESSIONAL_PERMISSION_KEYS.LEAVES]: "/ausencias",
  [PROFESSIONAL_PERMISSION_KEYS.SCHEDULE]: "/horario"
};

type RouteRule = {
  matches: (pathname: string) => boolean;
  permissions: ProfessionalPermissionKey[];
};

const ROUTE_RULES: RouteRule[] = [
  {
    permissions: [PROFESSIONAL_PERMISSION_KEYS.RESERVATIONS],
    matches: (pathname) => pathname === "/reservas"
  },
  {
    permissions: [PROFESSIONAL_PERMISSION_KEYS.LEAVES],
    matches: (pathname) => pathname === "/ausencias"
  },
  {
    permissions: [PROFESSIONAL_PERMISSION_KEYS.SCHEDULE],
    matches: (pathname) => pathname === "/horario"
  },
  {
    permissions: [PROFESSIONAL_PERMISSION_KEYS.SCHEDULE],
    matches: (pathname) => pathname === "/tareas"
  },
  {
    permissions: [PROFESSIONAL_PERMISSION_KEYS.CLIENTS, PROFESSIONAL_PERMISSION_KEYS.CLIENT_HISTORY],
    matches: (pathname) => pathname === "/clientes"
  }
];

export function getRequiredPermissionsForPath(pathname: string) {
  const normalized = pathname.split("?")[0] || "/";
  const match = ROUTE_RULES.find((rule) => rule.matches(normalized));
  return match?.permissions ?? [];
}

export function canAccessPath(pathname: string, permissions: Iterable<string>) {
  const normalized = pathname.split("?")[0] || "/";
  if (PUBLIC_PATHS.has(normalized)) {
    return true;
  }

  const requiredPermissions = getRequiredPermissionsForPath(normalized);
  if (requiredPermissions.length === 0) {
    return true;
  }

  const permissionSet = new Set(permissions);
  return requiredPermissions.some((permission) => permissionSet.has(permission));
}

export function getDefaultAuthorizedRoute(permissions: Iterable<string>) {
  const permissionSet = new Set(permissions);

  for (const permission of DEFAULT_ROUTE_ORDER) {
    if (permissionSet.has(permission)) {
      return DEFAULT_ROUTE_BY_PERMISSION[permission];
    }
  }

  return NO_ACCESS_ROUTE;
}
